import { apiClient } from './api';

// ==========================================
// TypeScript Interfaces
// ==========================================

export interface ModernizationStrategy {
  architectureStyle: 'monolithic' | 'microservices';
  eventDriven: boolean;
  messageBroker?: 'kafka' | 'rabbitmq' | 'redis' | 'none';
  cloudPlatform: 'aws' | 'gcp' | 'azure' | 'on_premise';
  mobileAppNeeded: boolean;
  mobileStacks?: ('ios_native' | 'android_native' | 'react_native' | 'flutter')[];
  backendLanguages: ('java' | 'kotlin' | 'python' | 'nodejs' | 'go' | 'csharp')[];
  frontendFramework?: 'react' | 'angular' | 'vue' | 'svelte' | 'nextjs' | 'none';
  primaryDatabase: 'postgresql' | 'mysql' | 'mongodb' | 'dynamodb' | 'cosmosdb' | 'firestore';
  serviceBus?: 'aws_sqs' | 'azure_service_bus' | 'google_pubsub' | 'none';
  additionalTech?: Record<string, any>;
}

export interface ModernizationProject {
  id: string;
  name: string;
  description?: string;
  project_type: string;

  // Source information
  source_type?: string;
  source_location?: string;
  source_branch?: string;

  // Analysis status
  analysis_status?: string;

  // Project linking
  linked_analysis_project_id?: string;
  linked_analysis_project_name?: string;

  // Wizard state
  current_step?: number;
  wizard_state?: Record<string, any>;

  // Strategy and goals
  strategy?: ModernizationStrategy;
  modernization_goals?: string;

  // Status and progress
  modernization_status?: 'planning' | 'in_progress' | 'review' | 'completed' | 'failed';
  phase?: number;
  total_phases?: number;
  tasks_completed?: number;
  tasks_total?: number;
  progress_percentage?: number;

  // Generated artifacts
  plan_id?: string;
  plan_content?: string;
  adr_session_id?: string;
  adrs?: any[];
  repository_config?: Record<string, any>;

  // Tech stack
  frontend_stack?: Record<string, any>;
  backend_stack?: Record<string, any>;
  database_stack?: Record<string, any>;
  infrastructure_stack?: Record<string, any>;

  // Timestamps
  created_at: string;
  updated_at: string;
  analyzed_at?: string;
  started_at?: string;
  completed_at?: string;

  // Ownership
  created_by: string;
  updated_by?: string;
}

export interface ModernizationProjectSummary {
  id: string;
  name: string;
  description?: string;
  project_type: string;
  linked_analysis_project_id?: string;
  linked_analysis_project_name?: string;
  current_step?: number;
  modernization_status?: string;
  progress_percentage?: number;
  phase?: number;
  total_phases?: number;
  architecture_style?: string;
  cloud_platform?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateModernizationProjectRequest {
  name: string;
  description?: string;
  source_type?: string;
  source_location?: string;
  source_branch?: string;
  project_type?: string;
  linked_analysis_project_id?: string;
  current_step?: number;
  wizard_state?: Record<string, any>;
  strategy?: ModernizationStrategy;
  modernization_goals?: string;
}

export interface WizardStateUpdate {
  current_step: number;
  wizard_state: Record<string, any>;
  strategy?: ModernizationStrategy;
  modernization_goals?: string;
  plan_content?: string;
  plan_id?: string;
  adr_session_id?: string;
  adrs?: any[];
  repository_config?: Record<string, any>;
}

export interface ModernizationProjectUpdate {
  name?: string;
  description?: string;
  modernization_status?: string;
  phase?: number;
  total_phases?: number;
  tasks_completed?: number;
  tasks_total?: number;
  progress_percentage?: number;
  started_at?: string;
  completed_at?: string;
}

export interface ModernizationProjectListResponse {
  total: number;
  projects: ModernizationProjectSummary[];
}

export interface AutoSaveStatusResponse {
  success: boolean;
  project_id: string;
  current_step: number;
  last_saved_at: string;
  message: string;
}

export interface ArtifactsResponse {
  project_id: string;
  plan?: {
    plan_id?: string;
    content?: string;
  };
  adrs?: any[];
  repository_config?: Record<string, any>;
  generated_files?: any[];
}

/**
 * Modernization Projects Service
 * Handles CRUD operations, auto-save, project linking, and resume functionality
 */
export const modernizationService = {
  /**
   * Create a new modernization project
   */
  async createProject(data: CreateModernizationProjectRequest): Promise<ModernizationProject> {
    const response = await apiClient.post<ModernizationProject>(
      '/modernization/projects',
      data
    );
    return response.data;
  },

  /**
   * Get all modernization projects
   */
  async getProjects(
    projectType?: string,
    status?: string,
    skip = 0,
    limit = 100
  ): Promise<ModernizationProjectListResponse> {
    const params: Record<string, any> = { skip, limit };
    if (projectType) params.project_type = projectType;
    if (status) params.status = status;

    const response = await apiClient.get<ModernizationProjectListResponse>(
      '/modernization/projects',
      { params }
    );
    return response.data;
  },

  /**
   * Get a specific modernization project
   */
  async getProject(id: string): Promise<ModernizationProject> {
    const response = await apiClient.get<ModernizationProject>(
      `/modernization/projects/${id}`
    );
    return response.data;
  },

  /**
   * Update project fields
   */
  async updateProject(
    id: string,
    data: ModernizationProjectUpdate
  ): Promise<ModernizationProject> {
    const response = await apiClient.put<ModernizationProject>(
      `/modernization/projects/${id}`,
      data
    );
    return response.data;
  },

  /**
   * Auto-save wizard state (called every 3 seconds)
   */
  async updateWizardState(
    id: string,
    wizardState: WizardStateUpdate
  ): Promise<AutoSaveStatusResponse> {
    const response = await apiClient.put<AutoSaveStatusResponse>(
      `/modernization/projects/${id}/wizard-state`,
      wizardState
    );
    return response.data;
  },

  /**
   * Update current wizard step
   */
  async updateCurrentStep(id: string, step: number): Promise<ModernizationProject> {
    const response = await apiClient.patch<ModernizationProject>(
      `/modernization/projects/${id}/step`,
      { step }
    );
    return response.data;
  },

  /**
   * Link modernization project to analysis project
   */
  async linkToAnalysisProject(
    modernizationId: string,
    analysisId: string
  ): Promise<ModernizationProject> {
    const response = await apiClient.post<ModernizationProject>(
      `/modernization/projects/${modernizationId}/link`,
      { analysis_project_id: analysisId }
    );
    return response.data;
  },

  /**
   * Delete a modernization project
   */
  async deleteProject(id: string): Promise<void> {
    await apiClient.delete(`/modernization/projects/${id}`);
  },

  /**
   * Get all artifacts for a project
   */
  async getArtifacts(id: string): Promise<ArtifactsResponse> {
    const response = await apiClient.get<ArtifactsResponse>(
      `/modernization/projects/${id}/artifacts`
    );
    return response.data;
  },
};
